<!-- Spotlight Overlay -->
<div
    *ngIf="isOpen"
    class="spotlight-overlay fixed inset-0 z-[999] flex items-start justify-center pt-[10vh] bg-black/50 backdrop-blur-sm transition-opacity duration-200 opacity-100"
    (click)="onOverlayClick($event)">

    <!-- Spotlight Container -->
    <div
        class="spotlight-container w-full max-w-[700px] mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-200 scale-100 animate-fadeIn">

        <!-- Header with User Info -->
        <div class="px-8 pt-8 pb-4">
            <div class="flex items-center gap-4">
                <!-- User Avatar -->
                <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-lg font-semibold flex-shrink-0 shadow-lg overflow-hidden">
                    <ng-container *ngIf="getUserAvatar()">
                        <img [src]="getUserAvatar()" [alt]="getUserFirstName()" class="w-full h-full object-cover">
                    </ng-container>
                    <ng-container *ngIf="!getUserAvatar()">
                        {{ getUserInitials() }}
                    </ng-container>
                </div>
                <!-- Greeting -->
                <div class="flex-1">
                    <h2 class="text-[20px] font-remote font-[600] text-gray-800">Olá, {{ getUserFirstName() }}</h2>
                </div>
                <!-- Close Button -->
                <button (click)="close()" class="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                    <mat-icon class="icon-size-5 text-gray-400">close</mat-icon>
                </button>
            </div>
            <!-- Question Text with Gradient - Centered -->
            <p class="text-[22px] font-inter font-[500] text-center mt-6 bg-gradient-to-r from-blue-600 to-pink-500 bg-clip-text text-transparent">
                Como posso te ajudar hoje?
            </p>
        </div>

        <!-- Search Input -->
        <div class="px-8 pb-6">
            <div class="flex items-center px-5 py-4 bg-gray-50 border border-gray-200 rounded-xl focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
                <span class="text-lg mr-3">✨</span>
                <input
                    #searchInput
                    type="text"
                    [(ngModel)]="searchQuery"
                    (ngModelChange)="onInputChange()"
                    placeholder="Pergunte qualquer coisa..."
                    class="flex-1 text-[16px] font-inter text-gray-900 placeholder-gray-400 outline-none bg-transparent"
                    autocomplete="off"
                    spellcheck="false" />
                <div *ngIf="searchQuery" (click)="clearSearch()" class="p-1 rounded-full hover:bg-gray-200 cursor-pointer mr-2">
                    <mat-icon class="icon-size-4 text-gray-400">close</mat-icon>
                </div>
                <button
                    (click)="performSearch()"
                    [disabled]="!searchQuery.trim() || state === 'searching'"
                    class="w-10 h-10 flex items-center justify-center bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 rounded-lg transition-colors">
                    <mat-icon *ngIf="state !== 'searching'" class="icon-size-5 text-white">send</mat-icon>
                    <mat-spinner *ngIf="state === 'searching'" [diameter]="18"></mat-spinner>
                </button>
            </div>
        </div>

        <!-- Content Area -->
        <div class="max-h-[450px] overflow-y-auto px-8 pb-6">

            <!-- Chat Section (appears when searching, response or error) -->
            <ng-container *ngIf="state === 'searching' || state === 'response' || state === 'error'">
                <!-- User Question Bubble -->
                <div class="flex justify-end mb-4 animate-slideUpFadeIn">
                    <div class="max-w-[80%] px-4 py-3 bg-blue-600 text-white rounded-2xl rounded-tr-md">
                        <span class="text-[14px] font-inter">{{ lastQuery }}</span>
                    </div>
                </div>

                <!-- AI Searching/Typing Indicator -->
                <div *ngIf="state === 'searching'" class="mb-6 animate-slideUpFadeIn [animation-delay:150ms]">
                    <div class="flex items-start gap-3">
                        <!-- AI Avatar -->
                        <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                            <span class="text-white text-sm">✨</span>
                        </div>
                        <!-- Typing Indicator -->
                        <div class="bg-gray-100 rounded-2xl rounded-tl-md px-5 py-4">
                            <div class="flex items-center gap-1">
                                <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0ms"></div>
                                <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 150ms"></div>
                                <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 300ms"></div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- AI Response -->
                <div *ngIf="state === 'response' && currentResponse" class="mb-6 animate-slideUpFadeIn [animation-delay:150ms]">
                    <div class="flex items-start gap-3">
                        <!-- AI Avatar -->
                        <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                            <span class="text-white text-sm">✨</span>
                        </div>
                        <!-- Response Content -->
                        <div class="flex-1">
                            <!-- Member Profile Display -->
                            <spotlight-result-member-profile
                                *ngIf="isMemberProfile && tableData.length > 0"
                                [title]="currentResponse.resultTitle"
                                [description]="currentResponse.resultDescription"
                                [data]="tableData[0]"
                                [executionTimeMs]="currentResponse.executionTimeMs">
                            </spotlight-result-member-profile>

                            <!-- Counter Display -->
                            <spotlight-result-counter
                                *ngIf="!isMemberProfile && resultDisplayType === 'counter' && tableData.length > 0"
                                [title]="currentResponse.resultTitle"
                                [description]="currentResponse.resultDescription"
                                [value]="tableData[0][tableHeaders[0]?.key]"
                                [executionTimeMs]="currentResponse.executionTimeMs">
                            </spotlight-result-counter>

                            <!-- Monetary Display -->
                            <spotlight-result-monetary
                                *ngIf="!isMemberProfile && resultDisplayType === 'monetary' && tableData.length > 0"
                                [title]="currentResponse.resultTitle"
                                [description]="currentResponse.resultDescription"
                                [value]="tableData[0][tableHeaders[0]?.key]"
                                [executionTimeMs]="currentResponse.executionTimeMs">
                            </spotlight-result-monetary>

                            <!-- Multi Column Display -->
                            <spotlight-result-multi-column
                                *ngIf="!isMemberProfile && resultDisplayType === 'multiColumn' && tableData.length > 0"
                                [title]="currentResponse.resultTitle"
                                [description]="currentResponse.resultDescription"
                                [data]="tableData[0]"
                                [executionTimeMs]="currentResponse.executionTimeMs">
                            </spotlight-result-multi-column>

                            <!-- List Display -->
                            <spotlight-result-list
                                *ngIf="resultDisplayType === 'list'"
                                [title]="currentResponse.resultTitle"
                                [description]="currentResponse.resultDescription"
                                [data]="allTableData"
                                [executionTimeMs]="currentResponse.executionTimeMs"
                                [pageSize]="PAGE_SIZE">
                            </spotlight-result-list>

                            <!-- Follow Up Suggestion -->
                            <div *ngIf="currentResponse.suggestedFollowup"
                                 (click)="selectFollowUp()"
                                 class="mt-3 flex items-center px-4 py-2.5 bg-gray-50 hover:bg-gray-100 rounded-xl cursor-pointer transition-colors border border-gray-200">
                                <mat-icon class="icon-size-4 text-blue-500 mr-2">lightbulb</mat-icon>
                                <span class="text-[12px] font-inter text-gray-600 flex-1">{{ currentResponse.suggestedFollowup }}</span>
                                <mat-icon class="icon-size-4 text-gray-400">arrow_forward</mat-icon>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Error State -->
                <div *ngIf="state === 'error'" class="mb-6 animate-slideUpFadeIn [animation-delay:150ms]">
                    <div class="flex items-start gap-3">
                        <!-- Error Avatar -->
                        <div class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                             [ngClass]="isRateLimitError ? 'bg-amber-100' : 'bg-red-100'">
                            <span class="text-sm">{{ isRateLimitError ? '⏳' : '⚠️' }}</span>
                        </div>
                        <!-- Error Content -->
                        <div class="flex-1 rounded-2xl rounded-tl-md p-4"
                             [ngClass]="isRateLimitError ? 'bg-amber-50' : 'bg-red-50'">
                            <p class="text-[13px] font-inter leading-relaxed"
                               [ngClass]="isRateLimitError ? 'text-amber-700' : 'text-red-600'">
                                {{ errorMessage }}
                            </p>
                            <button *ngIf="!isRateLimitError"
                                    (click)="retrySearch()"
                                    class="mt-3 px-3 py-1.5 bg-red-100 hover:bg-red-200 rounded-lg text-[12px] font-inter text-red-700 transition-colors">
                                Tentar novamente
                            </button>
                        </div>
                    </div>
                </div>
            </ng-container>

            <!-- Initial State: Suggestions & Recent Searches (only when initial) -->
            <ng-container *ngIf="state === 'initial'">
                <!-- Loading State -->
                <div *ngIf="loadingRecent && loadingSuggestions" class="py-8 flex flex-col items-center justify-center">
                    <mat-spinner [diameter]="24" class="mb-3"></mat-spinner>
                    <span class="text-[13px] font-inter text-gray-400">Carregando sugestões...</span>
                </div>

                <!-- Suggestions as Cards -->
                <div *ngIf="!loadingSuggestions && suggestions.length > 0" class="mb-6">
                    <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Sugestões</div>
                    <div class="grid grid-cols-2 gap-3">
                        <div *ngFor="let item of suggestions; let i = index"
                             (click)="selectItem(item)"
                             class="p-4 bg-gradient-to-br from-gray-50 to-gray-100 hover:from-blue-50 hover:to-indigo-50 rounded-xl cursor-pointer transition-all border border-gray-200 hover:border-blue-300 hover:shadow-md"
                             [class.ring-2]="isSelected('suggestion', i)"
                             [class.ring-blue-400]="isSelected('suggestion', i)">
                            <span class="text-xl mb-2 block">{{ getSearchIcon(item.question) }}</span>
                            <span class="text-[13px] font-inter text-gray-700 leading-snug line-clamp-2">{{ item.question }}</span>
                        </div>
                    </div>
                </div>

                <!-- Recent Searches as List -->
                <div *ngIf="!loadingRecent && recentSearches.length > 0">
                    <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Perguntas recentes</div>
                    <div class="space-y-1">
                        <div *ngFor="let item of recentSearches; let i = index"
                             (click)="selectItem(item)"
                             class="flex items-center px-3 py-2.5 rounded-lg cursor-pointer transition-colors hover:bg-gray-50"
                             [class.bg-blue-50]="isSelected('recent', i)">
                            <mat-icon class="icon-size-4 text-gray-400 mr-3">history</mat-icon>
                            <span class="text-[13px] font-inter text-gray-600 flex-1">{{ item.question }}</span>
                            <mat-icon class="icon-size-4 text-gray-300">chevron_right</mat-icon>
                        </div>
                    </div>
                </div>

                <!-- Empty State -->
                <div *ngIf="!loadingRecent && !loadingSuggestions && recentSearches.length === 0 && suggestions.length === 0"
                     class="py-8 flex flex-col items-center justify-center">
                    <span class="text-4xl mb-3">💡</span>
                    <span class="text-[14px] font-inter text-gray-500">Digite uma pergunta para começar</span>
                    <span class="text-[12px] font-inter text-gray-400 mt-1">Ex: "Quantos membros ativos temos?"</span>
                </div>
            </ng-container>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-between px-8 py-4 border-t border-gray-100 bg-gray-50">
            <div class="flex items-center gap-3">
                <div class="flex items-center gap-1">
                    <span class="px-1.5 py-0.5 bg-gray-200 rounded text-[10px] text-gray-500 font-medium">Enter</span>
                    <span class="text-[10px] text-gray-400 ml-1">enviar</span>
                </div>
                <div class="flex items-center gap-1">
                    <span class="px-1.5 py-0.5 bg-gray-200 rounded text-[10px] text-gray-500 font-medium">ESC</span>
                    <span class="text-[10px] text-gray-400 ml-1">fechar</span>
                </div>
            </div>
            <div class="flex items-center gap-1.5">
                <span class="text-[10px] text-gray-400">Powered by</span>
                <!-- Groq -->
                <ng-container *ngIf="currentResponse?.llmProvider === 'Groq'">
                    <img src="assets/icons/groq-icon.svg" alt="Groq" class="w-3.5 h-3.5">
                    <span class="text-[10px] font-semibold text-gray-500">Groq</span>
                </ng-container>
                <!-- Gemini -->
                <ng-container *ngIf="currentResponse?.llmProvider === 'Gemini'">
                    <img src="assets/icons/google-gemini-icon.svg" alt="Gemini" class="w-3.5 h-3.5">
                    <span class="text-[10px] font-semibold text-gray-500">Gemini</span>
                </ng-container>
                <!-- Default AI -->
                <ng-container *ngIf="!currentResponse?.llmProvider">
                    <span class="text-[10px] font-semibold text-gray-500">AI</span>
                </ng-container>
            </div>
        </div>
    </div>
</div>
